import * as React from "react";
import { Pencil, Shield } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * Shared building blocks for the Settings → Nutrition screens. Extracted to
 * avoid hand-rolled duplicates of the same access-denied / empty-state /
 * required-asterisk patterns across food-library forms and tables.
 */

export const RequiredMark: React.FC<{ className?: string }> = ({
  className,
}) => (
  <span aria-hidden="true" className={cn("text-destructive", className)}>
    *
  </span>
);

interface AccessDeniedCardProps {
  feature: string;
}

export const AccessDeniedCard: React.FC<AccessDeniedCardProps> = ({
  feature,
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-destructive" />
        Access Denied
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-center py-8">
        <Shield className="h-16 w-16 text-destructive mx-auto mb-4" />
        <h3 className="z-h3 text-foreground mb-2">Permission Required</h3>
        <p className="z-body text-muted-foreground mb-4">
          Your current permissions do not allow access to the {feature} feature.
        </p>
        <p className="z-caption text-muted-foreground">
          Please contact your administrator if you believe this is an error.
        </p>
      </div>
    </CardContent>
  </Card>
);

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  className,
}) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center py-12 text-center",
      className
    )}
  >
    <div className="text-muted-foreground/50 mb-4">{icon}</div>
    <h3 className="z-h3 text-foreground mb-1">{title}</h3>
    {description ? (
      <p className="z-caption text-muted-foreground">{description}</p>
    ) : null}
  </div>
);

/**
 * Version-state chrome, shared by recipes and plain foods.
 *
 * Both carry the same draft → publish model (epic ZCS-403), so both need the
 * same two pieces of chrome. These lived inside AddRecipeForm until foods
 * needed them; nothing about them was ever recipe-specific except the prop
 * that said so.
 */

interface FoodCodeBadgeProps {
  code: string | null;
  /**
   * Has this ever been published? A recipe passes `status === "submitted"`; a
   * plain food is always true, since foods do not start life as drafts.
   */
  isPublished: boolean;
  versionNumber?: number | null;
  /** What to call the not-yet-published state. Recipes call it "Draft". */
  unpublishedLabel?: string;
}

export const FoodCodeBadge: React.FC<FoodCodeBadgeProps> = ({
  code,
  isPublished,
  versionNumber,
  unpublishedLabel = "Draft",
}) => {
  // No food code until first publish — after which it stays STABLE across every
  // version and only the number advances. So an unpublished thing reads as
  // "Draft" and a published one as `Code · {code} · v{n}`.
  if (!isPublished || code === null) {
    return <Badge variant="warning">{unpublishedLabel}</Badge>;
  }
  return (
    <Badge variant="success">
      Code · {code}
      {versionNumber != null ? ` · v${versionNumber}` : ""}
    </Badge>
  );
};

interface DraftInProgressPillProps {
  /**
   * What the draft will become on publish. Best-effort: a publish that changes
   * nothing mints nothing, so this can be wrong even with one editor. Fine as
   * decoration, never assert it.
   */
  targetVersionNumber?: number | null;
  /**
   * Who staged the draft. Drafts are shared per item, not per user, so this is
   * attribution on a read-only screen and a warning inside an editor — pass it
   * there only for someone else's draft, where it is what stops one clinician
   * publishing another's staged edit unaware.
   */
  editorName?: string | null;
  /** Provide to make the pill route into the draft. Omit for a read-only marker in a list row. */
  onClick?: () => void;
  /** Tighter padding for table rows, where the pill sits inline beside a name. */
  compact?: boolean;
}

export const DraftInProgressPill: React.FC<DraftInProgressPillProps> = ({
  targetVersionNumber,
  editorName,
  onClick,
  compact = false,
}) => {
  const label = (
    <>
      Draft in progress
      {targetVersionNumber != null ? ` · v${targetVersionNumber}` : ""}
      {editorName ? ` · ${editorName}` : ""}
    </>
  );

  if (!onClick) {
    return (
      <span
        className={cn(
          "inline-flex items-center rounded-full border border-warning/40 bg-warning/10 z-hint font-medium text-warning",
          compact ? "px-1.5 py-0.5" : "min-h-6 gap-1.5 px-2.5 py-0.5"
        )}
      >
        {label}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded-full border border-warning/40 bg-warning/10 z-hint font-medium text-warning transition-colors hover:bg-warning/20",
        compact ? "px-1.5 py-0.5" : "min-h-6 gap-1.5 px-2.5 py-0.5"
      )}
    >
      <Pencil className="size-3" aria-hidden />
      {label}
    </button>
  );
};
